import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, BookOpen, Users, ArrowUp, BarChart2 } from 'lucide-react';
import { useAuthorStats } from '../hooks/useAuthorStats';

export default function AuthorAnalytics() {
  const { 
    loading,
    error,
    stats,
    topIdeas,
    recentMilestones,
    pollResults
  } = useAuthorStats();

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[200px]">
        <div className="w-8 h-8 border-4 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto p-6 bg-red-50 border border-red-200 rounded-lg text-red-700">
        {error}
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      <div className="flex items-center gap-4 mb-8">
        <h1 className="text-3xl font-bold" style={{ color: 'var(--color-font)' }}>Analytics</h1>
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center gap-3 mb-2">
            <BookOpen className="w-5 h-5 text-indigo-600" />
            <h3 className="font-semibold text-gray-900">Total Stories</h3>
          </div>
          <p className="text-3xl font-bold text-gray-900">{stats.totalIdeas}</p>
          <p className="text-sm text-gray-500 mt-1">Across all pen names</p>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center gap-3 mb-2">
            <Users className="w-5 h-5 text-indigo-600" />
            <h3 className="font-semibold text-gray-900">Total Followers</h3>
          </div>
          <p className="text-3xl font-bold text-gray-900">{stats.totalFollowers}</p>
          <p className="text-sm text-gray-500 mt-1">Combined audience size</p>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center gap-3 mb-2">
            <ArrowUp className="w-5 h-5 text-indigo-600" />
            <h3 className="font-semibold text-gray-900">Total Upvotes</h3>
          </div>
          <p className="text-3xl font-bold text-gray-900">{stats.totalVotes}</p>
          <p className="text-sm text-gray-500 mt-1">Across all stories</p>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center gap-3 mb-2">
            <BarChart2 className="w-5 h-5 text-indigo-600" />
            <h3 className="font-semibold text-gray-900">Active Polls</h3>
          </div>
          <p className="text-3xl font-bold text-gray-900">{stats.activePolls}</p>
          <p className="text-sm text-gray-500 mt-1">Awaiting responses</p>
        </div>
      </div>

      {/* Top Ideas */}
      <div className="bg-white rounded-lg shadow-sm mb-8">
        <div className="p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900">Top Performing Stories</h2>
        </div>
        <div className="p-6">
          <div className="divide-y">
            {topIdeas.map(idea => (
              <div key={idea.id} className="py-4 first:pt-0 last:pb-0">
                <Link 
                  to={`/idea/${idea.id}`}
                  className="flex items-start justify-between hover:bg-gray-50 p-2 rounded-lg"
                >
                  <div>
                    <h3 className="font-medium text-gray-900">{idea.title}</h3>
                    <p className="text-sm text-gray-500">
                      Posted as {idea.identity_username}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-medium text-gray-900">{idea.votes_count} votes</p>
                    <p className="text-sm text-gray-500">{idea.followers_count} followers</p>
                  </div>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Recent Milestones */}
      <div className="bg-white rounded-lg shadow-sm mb-8">
        <div className="p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900">Recent Milestones</h2>
        </div>
        <div className="p-6">
          <div className="divide-y">
            {recentMilestones.map(milestone => (
              <div key={milestone.id} className="py-4 first:pt-0 last:pb-0">
                <Link 
                  to={`/idea/${milestone.idea_id}`}
                  className="block hover:bg-gray-50 p-2 rounded-lg"
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-medium text-gray-900">{milestone.title}</h3>
                      <p className="text-sm text-gray-500">
                        For {milestone.idea_title}
                      </p>
                    </div>
                    <span className={`px-2 py-1 text-sm rounded-full ${
                      milestone.status === 'completed' 
                        ? 'bg-green-100 text-green-800'
                        : milestone.status === 'in_progress'
                        ? 'bg-blue-100 text-blue-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {milestone.status.replace('_', ' ')}
                    </span>
                  </div>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Poll Results */}
      <div className="bg-white rounded-lg shadow-sm">
        <div className="p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900">Poll Results</h2>
        </div>
        <div className="p-6">
          <div className="divide-y">
            {pollResults.map(poll => (
              <div key={poll.id} className="py-4 first:pt-0 last:pb-0">
                <Link 
                  to={`/idea/${poll.idea_id}`}
                  className="block hover:bg-gray-50 p-2 rounded-lg"
                >
                  <div className="mb-2">
                    <div className="flex items-center justify-between">
                      <h3 className="font-medium text-gray-900">{poll.title}</h3>
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          poll.closed || new Date(poll.ends_at) < new Date()
                            ? 'bg-gray-100 text-gray-800'
                            : 'bg-green-100 text-green-800'
                        }`}>
                          {poll.closed || new Date(poll.ends_at) < new Date() ? 'Closed' : 'Active'}
                        </span>
                        <span className="text-sm text-gray-500">
                          {poll.total_votes} votes
                        </span>
                      </div>
                    </div>
                    <p className="text-sm text-gray-500">
                      For {poll.idea_title}
                    </p>
                  </div>
                  <div className="space-y-2">
                    {poll.options.map(option => (
                      <div key={option.id} className="flex items-center gap-2">
                        <div className="flex-1">
                          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-indigo-600 rounded-full"
                              style={{ width: `${poll.total_votes ? (option.votes_count / poll.total_votes) * 100 : 0}%` }}
                            />
                          </div>
                        </div>
                        <span className="text-sm text-gray-600 w-12 text-right">
                          {poll.total_votes ? Math.round((option.votes_count / poll.total_votes) * 100) : 0}%
                        </span>
                      </div>
                    ))}
                  </div>
                </Link>
              </div>
            ))}

            {pollResults.length === 0 && (
              <div className="text-center py-8">
                <BarChart2 className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No Polls Yet</h3>
                <p className="text-gray-600">
                  Create polls to gather feedback from your readers.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}